import React from 'react';

import PropTypes from 'prop-types';

import {
  getDistanceBetweenPoints,
  getPointRelativeToElement,
} from 'utilities/geometry';

const THRESHOLD = 150;
const RANGE = 80;
const SCROLL_RANGE = 5;
const COMPLETE = 1;
const SPEED = 0.065;

const pointFromTouch = touch => ({x: touch.clientX, y: touch.clientY});

class LongPressGesture extends React.Component {
  constructor() {
    super(...arguments);

    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleTick = this.handleTick.bind(this);

    this.startPoint = null;
    this.timer = null;
    this.progress = 0;
    this.startDate = null;
    this.isRecognizing = false;
    this.ref = React.createRef(null);
  }

  componentDidMount() {
    if (this.props.when) {
      this.listen();
    }
  }

  componentWillUnmount() {
    if (this.props.when) {
      this.unlisten();
    }
  }

  UNSAFE_componentWillReceiveProps(next) {
    if (this.props.when !== next.when) {
      if (next.when) {
        this.listen();
      } else {
        this.unlisten();
      }
    }
  }

  listen() {
    this.ref.current.addEventListener(
      'touchstart',
      this.handleTouchStart,
      false,
    );
    this.ref.current.addEventListener('touchmove', this.handleTouchMove, false);
    this.ref.current.addEventListener('touchend', this.handleTouchEnd, false);
  }

  unlisten() {
    this.ref.current.removeEventListener(
      'touchstart',
      this.handleTouchStart,
      false,
    );
    this.ref.current.removeEventListener(
      'touchmove',
      this.handleTouchMove,
      false,
    );
    this.ref.current.removeEventListener(
      'touchend',
      this.handleTouchEnd,
      false,
    );
  }

  handleTouchStart(event) {
    if (event.touches.length !== 1) return (this.isTouching = false);
    this.startDate = Date.now();
    this.isTouching = true;
    this.startTouchPoint = getPointRelativeToElement(
      pointFromTouch(event.touches[0]),
      this.ref.current,
    );
    this.startOffsetPoint = {
      x: window.scrollX,
      y: window.scrollY,
    };

    if (
      this.lastTouchPoint &&
      getDistanceBetweenPoints(this.startTouchPoint, this.lastTouchPoint) >
        RANGE
    ) {
      this.props.onLongPressCancel(
        this.lastTouchPoint.x,
        this.lastTouchPoint.y,
      );
      this.reset();
    }

    this.lastTouchPoint = this.startTouchPoint;

    if (!this.timer) {
      this.props.onLongPressStart(this.lastTouchPoint.x, this.lastTouchPoint.y);
      this.timer = setInterval(this.handleTick, 1000 / 60);
    }
  }

  handleTouchMove(event) {
    if (event.touches.length !== 1) return null;
    this.lastTouchPoint = getPointRelativeToElement(
      pointFromTouch(event.touches[0]),
      this.ref.current,
    );
  }

  handleTouchEnd(event) {
    if (event.touches.length !== 0) return null;

    this.isTouching = false;
  }

  handleTick() {
    if (!this.isRecognizing && Date.now() > this.startDate + THRESHOLD)
      return (this.isRecognizing = true);
    const distance = getDistanceBetweenPoints(
      this.startTouchPoint,
      this.lastTouchPoint,
    );
    const scrollDistance = getDistanceBetweenPoints(this.startOffsetPoint, {
      x: window.scrollX,
      y: window.scrollY,
    });
    if (this.isTouching && distance < RANGE && scrollDistance < SCROLL_RANGE) {
      this.tickUp();
    } else {
      this.tickDown();
    }
  }

  tickUp() {
    if (this.progress >= COMPLETE) {
      this.props.onLongPressProgress(
        this.lastTouchPoint.x,
        this.lastTouchPoint.y,
        1,
      );
      this.props.onLongPressEnd(this.lastTouchPoint.x, this.lastTouchPoint.y);

      this.reset();
    } else {
      this.progress += SPEED;
      this.props.onLongPressProgress(
        this.lastTouchPoint.x,
        this.lastTouchPoint.y,
        this.progress,
      );
    }
  }

  tickDown() {
    if (this.progress <= 0) {
      this.props.onLongPressCancel(
        this.lastTouchPoint.x,
        this.lastTouchPoint.y,
      );
      this.reset();
    } else {
      this.progress -= SPEED;
      this.props.onLongPressProgress(
        this.lastTouchPoint.x,
        this.lastTouchPoint.y,
        this.progress,
      );
    }
  }

  reset() {
    clearInterval(this.timer);
    this.timer = null;
    this.progress = 0;
    this.isRecognizing = false;
    this.startDate = null;
  }

  render() {
    const childrenArray = React.Children.toArray(this.props.children);

    if (childrenArray.length > 1) {
      throw new Error('LongPressGesture only accepts a single child element');
    }

    return React.cloneElement(childrenArray[0], {
      ref: this.ref,
    });
  }
}

LongPressGesture.propTypes = {
  onLongPressStart: PropTypes.func.isRequired,
  onLongPressProgress: PropTypes.func.isRequired,
  onLongPressCancel: PropTypes.func.isRequired,
  onLongPressEnd: PropTypes.func.isRequired,
};

export default LongPressGesture;
